import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import arrowWhiteIcon from 'assets/arrow-white.svg';
import helpButtonIcon from 'assets/helpButton.svg';
import sortIcon from 'assets/sort-icon.svg';
import stopIcon from 'assets/stop-icon.svg';

import { ToolTip } from 'Components/Tooltip';

import { AskAnything } from 'Pages/TeamDashboard/CreateReview/components/AskAnything';
import askAnythingStyles from 'Pages/TeamDashboard/CreateReview/components/AskAnything/styles.module.scss';
import {
  ASK_ANYTHING_TABS, SORT_MODES, supportsAskAnything,
} from 'Pages/TeamDashboard/CreateReview/components/constants';
import { DisclaimerFooter } from 'Pages/TeamDashboard/CreateReview/components/DisclaimerFooter';
import { FeedbackBox } from 'Pages/TeamDashboard/CreateReview/components/FeedbackBox';
import panelStyles from 'Pages/TeamDashboard/CreateReview/components/styles.module.scss';

import { getConversationMessages, listConversations } from 'services/conversationService';

import { FEEDBACK_EDGE_CASES } from 'utlis/constants';
import { getBackendFeedbackId, getFeedbackDisplayNumber } from 'utlis/feedbackNumbering';
import { mapChatMessage } from 'utlis/parseChatOverview';

import styles from './styles.module.scss';

const INTERSECTION_THRESHOLD_COUNT = 21;
const COMPOSER_MAX_LINES = 10;
const COMPOSER_COMPACT_LINE_LIMIT = 2;
const COMPOSER_FALLBACK_LINE_HEIGHT = 20;

function isFeedbackInspectable(item, totalImages) {
  if (!item?.page) {
    return false;
  }

  if (totalImages <= 0) {
    return true;
  }

  const pageIndex = item.page - 1;

  return pageIndex >= 0 && pageIndex < totalImages;
}

export const FeedbackList = ({
  feedback,
  sortedFeedback,
  displayNumberMap,
  sortMode,
  onSortModeChange,
  categoryName,
  watchDemo,
  isPublicView = false,
  onInspectFeedback,
  totalImages = 0,
  userClickSupersedesScrollRef,
  selectedFeedbackIndex = null,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const [showPopUp, setShowPopUp] = useState(false);
  const [activeTab, setActiveTab] = useState(ASK_ANYTHING_TABS.FINDINGS);
  const [askInputValue, setAskInputValue] = useState('');
  const [isInputHovering, setIsInputHovering] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatNextBeforeSeq, setChatNextBeforeSeq] = useState(null);
  const [isConversationLoaded, setIsConversationLoaded] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isChatThinking, setIsChatThinking] = useState(false);
  const [composerLineCount, setComposerLineCount] = useState(1);
  const [inputFooterHeight, setInputFooterHeight] = useState(90);

  const listContainerRef = useRef(null);
  const cardRefsRef = useRef([]);
  const dominantIndexRef = useRef(null);
  const visibleAreaByIndexRef = useRef({});
  const observerRef = useRef(null);
  const internalClickSupersedesScrollRef = useRef(false);
  const clickSupersedesScrollRef = userClickSupersedesScrollRef ?? internalClickSupersedesScrollRef;
  const programmaticScrollRef = useRef(false);
  const skipNextScrollIntoViewRef = useRef(false);
  const isFirstSortModeEffectRef = useRef(true);
  const skipInitialScrollIntoViewRef = useRef(true);
  const sendMessageRef = useRef(null);
  const pendingSendRef = useRef(false);
  const sortDropdownRef = useRef(null);
  const askInputRef = useRef(null);
  const inputFooterRef = useRef(null);
  const pendingScrollIndexRef = useRef(null);

  const reviewType = currentRecord?.extra_data?.template ?? currentRecord?.template;
  const showAskAnythingPanel = supportsAskAnything(reviewType) && !isPublicView;
  const hasExistingChat = chatMessages.length > 0;
  const isComposerExpanded = composerLineCount > COMPOSER_COMPACT_LINE_LIMIT;
  const isSingleLineComposer = composerLineCount <= 1;

  const feedbackById = useMemo(() => {
    const map = new Map();

    feedback.forEach(item => {
      const feedbackId = getBackendFeedbackId(item);

      if (feedbackId == null) {
        return;
      }

      const isNewIdea = item.isDeeperInsight
        || item.category === 'deeper_insight'
        || item.category === 'deeper-insights';

      if (!map.has(feedbackId) || isNewIdea) {
        map.set(feedbackId, item);
      }
    });

    return map;
  }, [feedback]);

  const syncComposerHeight = useCallback(() => {
    const textarea = askInputRef.current;

    if (!textarea) {
      return;
    }

    const computed = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computed.lineHeight) || COMPOSER_FALLBACK_LINE_HEIGHT;
    const padding = (parseFloat(computed.paddingTop) || 0) + (parseFloat(computed.paddingBottom) || 0);
    const maxHeight = (lineHeight * COMPOSER_MAX_LINES) + padding;

    textarea.style.overflowY = 'hidden';
    textarea.style.height = 'auto';

    const scrollHeight = textarea.scrollHeight;
    const nextHeight = Math.min(scrollHeight, maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';

    if (scrollHeight > maxHeight && textarea.selectionStart === textarea.value.length) {
      textarea.scrollTop = textarea.scrollHeight;
    }

    const lineCount = Math.max(1, Math.round((scrollHeight - padding) / lineHeight));

    setComposerLineCount(prev => (prev === lineCount ? prev : lineCount));
  }, []);

  // Grow with the typed text, and collapse to one line when the field is cleared or sent.
  // Re-measure after the single-line padding or the expanded width changes.
  useLayoutEffect(() => {
    if (!showAskAnythingPanel) {
      return;
    }

    syncComposerHeight();
  }, [askInputValue, isComposerExpanded, isSingleLineComposer, showAskAnythingPanel, syncComposerHeight]);

  // Rewrap when the composer width changes, without reacting to its own height
  useEffect(() => {
    const textarea = askInputRef.current;

    if (!textarea || !showAskAnythingPanel) {
      return undefined;
    }

    let lastWidth = textarea.clientWidth;
    // eslint-disable-next-line compat/compat -- ResizeObserver reflows the composer when its width changes
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth === lastWidth) {
        return;
      }

      lastWidth = textarea.clientWidth;
      syncComposerHeight();
    });

    observer.observe(textarea);

    return () => observer.disconnect();
  }, [showAskAnythingPanel, syncComposerHeight]);

  // Keep findings and chat above the absolute footer as the composer grows
  useEffect(() => {
    const footer = inputFooterRef.current;

    if (!footer || !showAskAnythingPanel) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = footer.offsetHeight;

      setInputFooterHeight(prev => (prev === nextHeight ? prev : nextHeight));
    };

    updateHeight();

    // eslint-disable-next-line compat/compat -- ResizeObserver tracks the growing composer footer
    const observer = new ResizeObserver(updateHeight);

    observer.observe(footer);

    return () => observer.disconnect();
  }, [showAskAnythingPanel]);

  // Close sort dropdown on click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load conversation data when this review's template supports Ask Audit
  useEffect(() => {
    if (!showAskAnythingPanel || !currentRecord?.uuid || isConversationLoaded) {
      return;
    }

    const loadConversation = async () => {
      try {
        const response = await listConversations(currentRecord.uuid);

        if (response.results && response.results.length > 0) {
          const conversation = response.results[0];

          setConversationId(conversation.uuid);

          if (conversation.message_count > 0) {
            const messagesResponse = await getConversationMessages(conversation.uuid);

            setChatMessages(messagesResponse.messages.map(mapChatMessage));
            setChatHasMore(messagesResponse.has_more);
            setChatNextBeforeSeq(messagesResponse.next_before_sequence);
          }
        }
      }
      catch {
        // Silently fail
      }
      finally {
        setIsConversationLoaded(true);
      }
    };

    loadConversation();
  }, [showAskAnythingPanel, currentRecord?.uuid, isConversationLoaded]);

  const handleSortSelect = mode => {
    onSortModeChange(mode);
    setIsSortDropdownOpen(false);
  };

  const handleInspectFromUserClick = useCallback(
    feedbackData => {
      onInspectFeedback?.(feedbackData);
    },
    [onInspectFeedback]
  );

  const handleNavigateToFeedback = useCallback(feedbackId => {
    const matched = feedbackById.get(feedbackId);
    const fallback = sortedFeedback.find(entry => entry.type === 'item')?.data;
    const item = matched || fallback;

    setActiveTab(ASK_ANYTHING_TABS.FINDINGS);

    if (item?.index == null) {
      return;
    }

    pendingScrollIndexRef.current = item.index;
    onInspectFeedback?.(item);
  }, [feedbackById, sortedFeedback, onInspectFeedback]);

  const handleSendMessage = useCallback(() => {
    if (!isConversationLoaded || !askInputValue.trim()) {
      return;
    }

    if (activeTab !== ASK_ANYTHING_TABS.ASK_AUDIT) {
      pendingSendRef.current = true;
      setActiveTab(ASK_ANYTHING_TABS.ASK_AUDIT);

      return;
    }

    if (sendMessageRef.current) {
      sendMessageRef.current();
    }
  }, [askInputValue, activeTab, isConversationLoaded]);

  const handleInputKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  useEffect(() => {
    if (activeTab === ASK_ANYTHING_TABS.ASK_AUDIT && pendingSendRef.current && isConversationLoaded) {
      pendingSendRef.current = false;

      const timer = setTimeout(() => {
        if (sendMessageRef.current) {
          sendMessageRef.current();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [activeTab, isConversationLoaded]);

  useEffect(() => {
    if (showPopUp) {
      const timer = setTimeout(() => {
        setShowPopUp(false);
      }, 5000); // Hide after 5 seconds

      // Cleanup the timer on component unmount or when showPopUp changes
      return () => clearTimeout(timer);
    }
  }, [showPopUp]);

  const feedbackIndexMap = useMemo(() => {
    const map = new Map();

    currentRecord.final_feedback.forEach((feedbackItem, index) => {
      map.set(feedbackItem, index);
    });

    return map;
  }, [currentRecord]);

  const errorCardValues = useMemo(() => {
    if (!feedback.length) {
      return FEEDBACK_EDGE_CASES.NO_FEEDBACK;
    }
    if (currentRecord?.extra_data?.page_type === null) {
      return FEEDBACK_EDGE_CASES.NOT_SUPPORTED;
    }

    return null;
  }, [currentRecord]);

  // Count dismissed feedbacks for public view
  const dismissedCount = useMemo(() => {
    if (!isPublicView) {
      return 0;
    }

    return feedback.filter(val => val.dismiss === true).length;
  }, [feedback, isPublicView]);

  const updateDominantCard = useCallback(
    (feedbackList, onInspect, imagesCount) => {
      if (clickSupersedesScrollRef.current) {
        return;
      }

      if (!feedbackList.length) {
        return;
      }

      const allIndices = [...feedbackList.keys()];
      const dominantIndex = allIndices.reduce((best, i) => {
        const area = visibleAreaByIndexRef.current[i] || 0;
        const bestArea = visibleAreaByIndexRef.current[best] || 0;

        return area > bestArea ? i : best;
      }, allIndices[0]);

      if (!isFeedbackInspectable(feedbackList[dominantIndex], imagesCount)) {
        return;
      }

      if (dominantIndex != null && dominantIndex !== dominantIndexRef.current && onInspect) {
        dominantIndexRef.current = dominantIndex;
        onInspect(feedbackList[dominantIndex]);
      }
    },
    []
  );

  useEffect(() => {
    // Findings unmounts on the Ask audit tab, so rebind when that list is shown again.
    if (activeTab !== ASK_ANYTHING_TABS.FINDINGS) {
      return undefined;
    }

    const container = listContainerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (programmaticScrollRef.current) {
        return;
      }

      clickSupersedesScrollRef.current = false;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab, clickSupersedesScrollRef]);

  // Sort change: jump the list to the top so the first card is in view
  useEffect(() => {
    if (isFirstSortModeEffectRef.current) {
      isFirstSortModeEffectRef.current = false;

      return undefined;
    }

    skipNextScrollIntoViewRef.current = true;
    clickSupersedesScrollRef.current = true;
    dominantIndexRef.current = null;

    const container = listContainerRef.current;

    if (!container) {
      return undefined;
    }

    programmaticScrollRef.current = true;
    container.scrollTop = 0;

    const timeoutId = setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [sortMode, clickSupersedesScrollRef]);

  // Scroll the selected feedback card into view when selection comes from pin click or card click.
  useEffect(() => {
    if (skipNextScrollIntoViewRef.current) {
      skipNextScrollIntoViewRef.current = false;

      return undefined;
    }

    // Keep the list at the top on open; the first card is already in view
    if (skipInitialScrollIntoViewRef.current) {
      if (selectedFeedbackIndex != null) {
        skipInitialScrollIntoViewRef.current = false;
      }

      return undefined;
    }

    if (
      selectedFeedbackIndex != null
      && selectedFeedbackIndex >= 0
      && selectedFeedbackIndex < feedback.length
      && cardRefsRef.current[selectedFeedbackIndex]
    ) {
      programmaticScrollRef.current = true;
      cardRefsRef.current[selectedFeedbackIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      const container = listContainerRef.current;
      let scrollEndTimer = null;

      const onScrollEnd = () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
          programmaticScrollRef.current = false;
          container?.removeEventListener('scroll', onScrollEnd);
        }, 150);
      };

      container?.addEventListener('scroll', onScrollEnd, { passive: true });

      // Safety fallback in case no scroll events fire (element already in view)
      const fallback = setTimeout(() => {
        programmaticScrollRef.current = false;
        container?.removeEventListener('scroll', onScrollEnd);
      }, 2000);

      return () => {
        clearTimeout(scrollEndTimer);
        clearTimeout(fallback);
        container?.removeEventListener('scroll', onScrollEnd);
      };
    }
  }, [selectedFeedbackIndex, feedback.length]);

  // Findings unmounts on Ask audit, so scroll after that list is showing again.
  useEffect(() => {
    if (activeTab !== ASK_ANYTHING_TABS.FINDINGS || pendingScrollIndexRef.current == null) {
      return;
    }

    const card = cardRefsRef.current[pendingScrollIndexRef.current];

    if (!card) {
      return;
    }

    pendingScrollIndexRef.current = null;
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeTab, selectedFeedbackIndex, sortedFeedback]);

  useEffect(() => {
    // Findings unmounts on the Ask audit tab, so observe the list again when it returns.
    if (
      activeTab !== ASK_ANYTHING_TABS.FINDINGS
      || !onInspectFeedback
      || !feedback.length
      || !listContainerRef.current
    ) {
      return undefined;
    }

    dominantIndexRef.current = null;
    visibleAreaByIndexRef.current = {};

    const container = listContainerRef.current;
    const thresholds = Array.from(
      { length: INTERSECTION_THRESHOLD_COUNT },
      // eslint-disable-next-line no-unused-vars -- index is used; first param is array element (none)
      (_, i) => i / (INTERSECTION_THRESHOLD_COUNT - 1)
    );

    // eslint-disable-next-line compat/compat -- IntersectionObserver used for scroll-dominant card; not targeting IE11
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const index = parseInt(entry.target.getAttribute('data-feedback-index'), 10);

          if (Number.isNaN(index) || index < 0 || index >= feedback.length) {
            return;
          }

          const area = entry.intersectionRect.width * entry.intersectionRect.height;

          visibleAreaByIndexRef.current[index] = area;
        });
        updateDominantCard(feedback, onInspectFeedback, totalImages);
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: thresholds,
      }
    );

    observerRef.current = observer;

    cardRefsRef.current.forEach(el => {
      if (el) {
        observer.observe(el);
      }
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [activeTab, feedback, sortMode, onInspectFeedback, totalImages, updateDominantCard]);

  const renderFindingsContent = () => {
    if (errorCardValues) {
      return (
        <div className={styles['error-container']}>
          <img className={styles['image']} src={errorCardValues.image} alt="a problem occurred" />
          <div className={styles['text-container']}>
            <h2 className={styles['title']}>
              {errorCardValues.title}
            </h2>
            <p className={styles['sub-title']}>
              {errorCardValues.description}
            </p>
            <div className={styles['bottom-container']}>
              {errorCardValues === FEEDBACK_EDGE_CASES.NOT_SUPPORTED && (
                <button className={styles['button']} onClick={watchDemo}>
                  Watch demo
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref={listContainerRef} className={cn(styles['feedback-container'])}>
        {sortedFeedback.map(entry => {
          if (entry.type === 'divider') {
            return (
              <div key={`divider-${entry.key}`} className={styles['sort-divider']}>
                <div className={styles['sort-divider-line']} />
                <span className={styles['sort-divider-text']}>
                  {entry.label}
                </span>
                <div className={styles['sort-divider-line']} />
              </div>
            );
          }

          const val = entry.data;
          const originalIndex = val.index !== undefined ? val.index : feedbackIndexMap.get(val);

          if (isPublicView && val.dismiss) {
            return null;
          }

          const currentDisplayNumber = getFeedbackDisplayNumber(
            val,
            displayNumberMap,
            originalIndex
          );

          return (
            <div
              key={`${sortMode}-${originalIndex}-${val.issue || val.detailed_feedback?.[0] || originalIndex}`}
              ref={el => {
                cardRefsRef.current[originalIndex] = el;
              }}
              data-feedback-index={originalIndex}
            >
              <FeedbackBox
                data={val}
                index={currentDisplayNumber}
                currentFeedback={originalIndex + 1}
                selectedCategory={categoryName}
                selectedLabelCategory="All themes"
                isPublicView={isPublicView}
                onInspectFeedback={handleInspectFromUserClick}
                totalImages={totalImages}
                isSelected={selectedFeedbackIndex === originalIndex}
              />
            </div>
          );
        })}
        {isPublicView && dismissedCount > 0 && (
          <div className="flex items-center gap-8 py-2 mb-24">
            <span className="font-normal text-sm leading-5 text-left" style={{ color: '#8F97A5' }}>
              {dismissedCount}
              {' '}
              {dismissedCount === 1 ? 'finding has' : 'findings have'}
              {' '}
              been hidden
            </span>
            <ToolTip
              title="Audit authors can hide findings based on relevance or accuracy"
              position="right"
              width="150px"
            >
              <img
                src={helpButtonIcon}
                alt="Help"
                className="cursor-pointer"
                style={{ width: '16px', height: '16px' }}
              />
            </ToolTip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        panelStyles.createReviewRightPanel,
        styles['feedback-page'],
        { [styles['feedback-container-with-footer']]: !showAskAnythingPanel },
        { [styles['feedback-container-with-input']]: showAskAnythingPanel }
      )}
      style={showAskAnythingPanel ? { paddingBottom: inputFooterHeight } : undefined}
    >
      {/* Header: Tabs + Sort dropdown */}
      <div className={askAnythingStyles['tabs-header']}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={cn(
              askAnythingStyles.tab,
              { [askAnythingStyles['tab--active']]: activeTab === ASK_ANYTHING_TABS.FINDINGS }
            )}
            onClick={() => setActiveTab(ASK_ANYTHING_TABS.FINDINGS)}
          >
            Findings
          </button>
          {showAskAnythingPanel && (
            <button
              type="button"
              className={cn(
                askAnythingStyles.tab,
                { [askAnythingStyles['tab--active']]: activeTab === ASK_ANYTHING_TABS.ASK_AUDIT }
              )}
              onClick={() => setActiveTab(ASK_ANYTHING_TABS.ASK_AUDIT)}
            >
              Ask audit
            </button>
          )}
        </div>
        {activeTab === ASK_ANYTHING_TABS.FINDINGS && (
          <div ref={sortDropdownRef} className={styles['sort-dropdown-wrapper']}>
            <button
              type="button"
              className={styles['sort-trigger']}
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
            >
              <span className={styles['sort-trigger-text']}>
                {sortMode === SORT_MODES.IMPACT ? 'Impact' : 'Effort'}
              </span>
              <img src={sortIcon} alt="Sort" className={styles['sort-trigger-icon']} />
            </button>
            {isSortDropdownOpen && (
              <div className={styles['sort-dropdown']}>
                <span className={styles['sort-dropdown-heading']}>
                  Sort by
                </span>
                <button
                  type="button"
                  className={cn(
                    styles['sort-dropdown-item'],
                    { [styles['sort-dropdown-item--active']]: sortMode === SORT_MODES.IMPACT }
                  )}
                  onClick={() => handleSortSelect(SORT_MODES.IMPACT)}
                >
                  Impact
                </button>
                <button
                  type="button"
                  className={cn(
                    styles['sort-dropdown-item'],
                    { [styles['sort-dropdown-item--active']]: sortMode === SORT_MODES.EFFORT }
                  )}
                  onClick={() => handleSortSelect(SORT_MODES.EFFORT)}
                >
                  Effort
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {activeTab === ASK_ANYTHING_TABS.FINDINGS && renderFindingsContent()}

      {activeTab === ASK_ANYTHING_TABS.ASK_AUDIT && showAskAnythingPanel && (
        <AskAnything
          onSendMessage={sendMessageRef}
          inputValue={askInputValue}
          setInputValue={setAskInputValue}
          isHovering={isInputHovering}
          setIsHovering={setIsInputHovering}
          onThinkingChange={setIsAiThinking}
          designReviewUuid={currentRecord?.uuid}
          conversationId={conversationId}
          setConversationId={setConversationId}
          chatMessages={chatMessages}
          setChatMessages={setChatMessages}
          chatHasMore={chatHasMore}
          setChatHasMore={setChatHasMore}
          chatNextBeforeSeq={chatNextBeforeSeq}
          setChatNextBeforeSeq={setChatNextBeforeSeq}
          streamingContent={streamingContent}
          setStreamingContent={setStreamingContent}
          isChatThinking={isChatThinking}
          setIsChatThinking={setIsChatThinking}
          isConversationLoaded={isConversationLoaded}
          onNavigateToFeedback={handleNavigateToFeedback}
        />
      )}

      {/* Footer: chat input for Ask Audit templates, disclaimer otherwise */}
      {showAskAnythingPanel
        ? (
          <div ref={inputFooterRef} className={askAnythingStyles['input-footer']}>
            <div
              className={cn(
                askAnythingStyles['input-wrapper'],
                { [askAnythingStyles['input-wrapper--expanded']]: isComposerExpanded }
              )}
            >
              <textarea
                ref={askInputRef}
                className={cn(
                  askAnythingStyles['input-field'],
                  { [askAnythingStyles['input-field--single-line']]: isSingleLineComposer },
                  { [askAnythingStyles['input-field--preview']]: isInputHovering }
                )}
                placeholder={!isConversationLoaded ? '' : (hasExistingChat ? 'Continue chat' : 'Ask anything')}
                value={askInputValue}
                onChange={e => {
                  setIsInputHovering(false);
                  setAskInputValue(e.target.value);
                }}
                onKeyDown={handleInputKeyDown}
                rows={1}
              />
              <button
                type="button"
                className={cn(
                  askAnythingStyles['send-button'],
                  {
                    [askAnythingStyles['send-button--active']]:
                      isConversationLoaded
                      && askInputValue.trim().length > 0
                      && !isInputHovering
                      && !isAiThinking,
                  },
                  { [askAnythingStyles['send-button--stop']]: isAiThinking }
                )}
                onClick={handleSendMessage}
                disabled={
                  !isConversationLoaded
                  || ((!askInputValue.trim() || isInputHovering) && !isAiThinking)
                }
              >
                <img
                  src={isAiThinking ? stopIcon : arrowWhiteIcon}
                  alt={isAiThinking ? 'Stop' : 'Send'}
                  className={askAnythingStyles['send-icon']}
                />
              </button>
            </div>
          </div>
        )
        : (
          <DisclaimerFooter />
        )}
    </div>
  );
};

FeedbackList.propTypes = {
  feedback: PropTypes.array.isRequired,
  sortedFeedback: PropTypes.array.isRequired,
  displayNumberMap: PropTypes.object,
  sortMode: PropTypes.string.isRequired,
  onSortModeChange: PropTypes.func.isRequired,
  categoryName: PropTypes.string.isRequired,
  watchDemo: PropTypes.func.isRequired,
  isPublicView: PropTypes.bool,
  onInspectFeedback: PropTypes.func,
  totalImages: PropTypes.number,
  userClickSupersedesScrollRef: PropTypes.shape({ current: PropTypes.bool }),
  selectedFeedbackIndex: PropTypes.number,
};
